import { Controller } from "@hotwired/stimulus"
import { Popover } from "bootstrap"

// A plain-text hint for the control it is attached to. The hint only means anything
// beside that control, so it lives and dies with that control: it opens while the
// control is hovered or focused, and leaves the moment the control does.
export default class extends Controller {
  static values = {
    content: String,
    placement: { type: String, default: 'top' }
  }

  connect() {
    this.hintOpen = false
    this.menuOpen = false
    this.showHint = this.showHint.bind(this)
    this.hideHint = this.hideHint.bind(this)
    this.hideHintWhenControlHidden = this.hideHintWhenControlHidden.bind(this)
    this.hideHintForMenu = this.hideHintForMenu.bind(this)
    this.restoreHintAfterMenu = this.restoreHintAfterMenu.bind(this)

    this.popover = new Popover(this.element, {
      // The hint is opened and closed from this controller instead of by Bootstrap's
      // own hover/focus listeners. Bootstrap keeps a single component instance per
      // element, so on a control that already hosts one — the editor's background
      // colour button is also a dropdown toggle — its listeners can't find this
      // popover and build a throwaway one per hover instead, each leaving its bubble
      // behind in the corner of the page with nothing holding a reference to close it.
      trigger: 'manual',
      html: false,
      placement: this.placementValue,
      content: this.contentValue || '',
      // A hint is one line of text. Bootstrap's default template would also promote
      // the control's `title` into a header above that line, printing the same hint
      // twice, stacked — so the header is left out of the template entirely.
      template: '<div class="popover" role="tooltip">' +
        '<div class="popover-arrow"></div>' +
        '<div class="popover-body"></div>' +
        '</div>',
      // These are plain-text hints, never interactive — tag them so they can't
      // swallow a click on what they overlap. A bottom-placed hint on a dropdown
      // toggle (e.g. the editor's background-colour picker) sits right over the menu
      // that unfolds under it. See `.popover--hint` in components/popovers.scss.
      customClass: 'popover--hint'
    })

    this.element.addEventListener('mouseenter', this.showHint)
    this.element.addEventListener('focus', this.showHint)
    this.element.addEventListener('mouseleave', this.hideHint)
    this.element.addEventListener('blur', this.hideHint)

    // A control can be its own dropdown toggle — the editor's background colour button
    // is one — and a hint placed below such a toggle lands squarely on the menu that
    // unfolds under it, hiding the very options the click asked for. The hint has
    // nothing left to explain once that menu is open, so it stands down for as long as
    // the menu is up. See `.popover--hint` in components/popovers.scss.
    this.element.addEventListener('show.bs.dropdown', this.hideHintForMenu)
    this.element.addEventListener('hidden.bs.dropdown', this.restoreHintAfterMenu)

    // A control can be hidden while the pointer still rests on it — the rich text
    // editor's bubble toolbar disappears the moment the selection collapses. A hidden
    // control never receives the mouseleave that would close its hint, and measures
    // 0x0 so Popper re-anchors that hint to the top-left corner of the viewport,
    // where it parks for the rest of the session. Watching whether the control is on
    // screen closes the hint that its own events no longer can.
    this.controlVisibility = new IntersectionObserver(this.hideHintWhenControlHidden)
    this.controlVisibility.observe(this.element)
  }

  disconnect() {
    this.element.removeEventListener('mouseenter', this.showHint)
    this.element.removeEventListener('focus', this.showHint)
    this.element.removeEventListener('mouseleave', this.hideHint)
    this.element.removeEventListener('blur', this.hideHint)
    this.element.removeEventListener('show.bs.dropdown', this.hideHintForMenu)
    this.element.removeEventListener('hidden.bs.dropdown', this.restoreHintAfterMenu)
    this.controlVisibility?.disconnect()
    this.popover?.dispose()
  }

  // Hovering and focusing overlap on a clicked control, so both ends are guarded:
  // reopening an open hint would rebuild its bubble and blink it, and Bootstrap has
  // no public way to ask whether the hint is currently up.
  showHint() {
    if (this.hintOpen || this.menuOpen || !this.controlRendered) return

    this.hintOpen = true
    this.popover.show()
  }

  hideHint() {
    if (!this.hintOpen) return

    this.hintOpen = false
    this.popover.hide()
  }

  hideHintWhenControlHidden() {
    if (this.controlRendered) return

    this.hideHint()
  }

  // Opening the menu is tracked here rather than read back off the toggle's
  // `aria-expanded`, which Bootstrap only sets *after* it focuses the toggle: on a
  // control whose mousedown is prevented to hold the editor's selection, that focus is
  // the first the button receives, and its focus event would raise the hint again over
  // the menu while the attribute still reads closed.
  hideHintForMenu() {
    this.menuOpen = true
    this.hideHint()
  }

  // Closing the menu puts the control back in charge of its own hint, but the events
  // that would raise it may already have passed: dismissing with Escape leaves the
  // button focused, and clicking a swatch leaves the pointer where the menu was.
  // Neither fires again, so the hint is restored here for a control the author is
  // demonstrably still on.
  restoreHintAfterMenu() {
    this.menuOpen = false

    if (!this.element.matches(':hover, :focus')) return

    this.showHint()
  }

  get controlRendered() {
    return this.element.getClientRects().length > 0
  }
}
